import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Copy, RefreshCw } from 'lucide-react';
import { getBroadcastRetries } from '@/services/api/broadcast';
import type { BroadcastRetry } from '@/types/broadcast';
import { formatDuration, formatInTz } from './utils/broadcastUtils';

const BRT_TZ = 'America/Sao_Paulo';

// ─── Local design primitives (copied from ViewBroadcastGroupModal; do NOT edit that file) ───

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
    return (
        <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 min-w-0">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{label}</p>
            <p className={`text-lg font-bold mt-0.5 truncate ${color ?? 'text-gray-900'}`}>{value}</p>
            {sub && <p className="text-[11px] text-gray-400 truncate mt-0.5">{sub}</p>}
        </div>
    );
}

function KpiCard({ label, value, color }: { label: string; value: string | number; color: string }) {
    return (
        <div className="flex-1 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-center min-w-0">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-0.5">{label}</p>
        </div>
    );
}

// ─── Status badge ────────────────────────────────────────────────────────────────────────────

const retryStatusColor: Record<BroadcastRetry['status'], string> = {
    retrying: 'bg-amber-100 text-amber-700 border-amber-200',
    sent:     'bg-emerald-100 text-emerald-700 border-emerald-200',
    failed:   'bg-red-100 text-red-700 border-red-200',
};

const retryStatusLabel: Record<BroadcastRetry['status'], string> = {
    retrying: 'Tentando',
    sent:     'Resolvido',
    failed:   'Falhou',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────────────────────

function timeOnTrack(item: BroadcastRetry): string {
    if (item.resolved_at) {
        const diffS = (new Date(item.resolved_at).getTime() - new Date(item.first_551_at).getTime()) / 1000;
        return formatDuration(diffS);
    }
    return 'em andamento';
}

function useDebounce<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
}

// ─── Page component ──────────────────────────────────────────────────────────────────────────

export default function BroadcastRetries() {
    const [date, setDate] = useState<string>(new Date().toLocaleDateString('en-CA'));
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const perPage = 50;

    const debouncedSearch = useDebounce(search, 300);

    // Reset page when filters change
    useEffect(() => { setPage(1); }, [date, statusFilter]);

    const { data, isLoading, error, isFetching, refetch } = useQuery({
        queryKey: ['broadcast-retries', date, statusFilter, page, perPage],
        queryFn: () =>
            getBroadcastRetries({
                date,
                status: statusFilter || undefined,
                page,
                per_page: perPage,
            }),
        staleTime: 1000 * 15,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });

    const items: BroadcastRetry[] = data?.items ?? [];
    const totalPages = data?.pages ?? 1;
    const total = data?.total ?? 0;

    // Auto-polling when any item is still retrying
    const hasRetrying = useMemo(() => items.some((i) => i.status === 'retrying'), [items]);

    useQuery({
        queryKey: ['broadcast-retries-poll', date, statusFilter, page, perPage],
        queryFn: () =>
            getBroadcastRetries({
                date,
                status: statusFilter || undefined,
                page,
                per_page: perPage,
            }),
        enabled: hasRetrying,
        refetchInterval: 15000,
        staleTime: 0,
        refetchOnWindowFocus: false,
    });

    // KPIs computed over the current page items
    const kpis = useMemo(() => {
        const resolved  = items.filter((i) => i.status === 'sent').length;
        const failed    = items.filter((i) => i.status === 'failed').length;
        const retrying  = items.filter((i) => i.status === 'retrying').length;
        const avgAttempts =
            items.length > 0
                ? (items.reduce((s, i) => s + i.attempt_count, 0) / items.length).toFixed(1)
                : '–';
        return { resolved, failed, retrying, avgAttempts };
    }, [items]);

    // Client-side search filter by lead_name or page_name
    const filteredItems = useMemo(() => {
        if (!debouncedSearch) return items;
        const q = debouncedSearch.toLowerCase();
        return items.filter(
            (i) =>
                (i.lead_name ?? '').toLowerCase().includes(q) ||
                (i.page_name ?? '').toLowerCase().includes(q)
        );
    }, [items, debouncedSearch]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold text-gray-900">Esteira de Retry 551</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Leads bloqueados por erro 551 aguardando reenvio</p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl gap-2"
                    onClick={() => refetch()}
                    disabled={isFetching}
                >
                    <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                    Atualizar
                </Button>
            </div>

            {/* KPIs */}
            {!isLoading && items.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <StatCard label="Total (página)" value={total.toLocaleString('pt-BR')} />
                    <KpiCard label="Resolvidos" value={kpis.resolved.toLocaleString('pt-BR')} color="text-emerald-600" />
                    <KpiCard label="Falharam" value={kpis.failed.toLocaleString('pt-BR')} color="text-red-500" />
                    <KpiCard label="Tentando" value={kpis.retrying.toLocaleString('pt-BR')} color="text-amber-600" />
                    <KpiCard label="Média tentativas" value={kpis.avgAttempts} color="text-gray-700" />
                </div>
            )}

            {/* Filters */}
            <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4">
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="flex flex-col gap-1 min-w-[160px]">
                        <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Data</label>
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="h-8 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
                        />
                    </div>

                    <div className="flex flex-col gap-1 min-w-[180px]">
                        <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="h-8 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
                        >
                            <option value="">Todos</option>
                            <option value="retrying">Apenas tentando</option>
                            <option value="sent">Resolvidos</option>
                            <option value="failed">Falharam</option>
                        </select>
                    </div>

                    <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
                        <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Busca</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Lead ou página..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 h-8 text-sm"
                            />
                        </div>
                    </div>

                    {hasRetrying && (
                        <div className="flex items-end pb-0.5">
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600">
                                <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                                polling 15s
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Loading */}
            {isLoading && (
                <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-12 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-7 w-7 border-2 border-gray-200 border-t-gray-800" />
                    <p className="ml-3 text-gray-500 text-sm">Carregando retries...</p>
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="rounded-2xl bg-red-50 border border-red-200/50 p-6">
                    <p className="text-red-700 font-medium text-sm">Erro ao carregar retries. Tente novamente.</p>
                </div>
            )}

            {/* Table */}
            {!isLoading && !error && (
                <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1100px] text-sm">
                            <thead className="bg-gray-50">
                                <tr className="border-b border-gray-100">
                                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Lead</th>
                                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Página</th>
                                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Grupo</th>
                                    <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Tentativas</th>
                                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Primeiro 551</th>
                                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Último retry</th>
                                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Resolvido em</th>
                                    <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Tempo na esteira</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredItems.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-400">
                                            {debouncedSearch
                                                ? `Nenhum resultado para "${debouncedSearch}".`
                                                : 'Nenhum retry encontrado para esta data e filtros.'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredItems.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50">
                                            {/* Lead */}
                                            <td className="px-4 py-3">
                                                <p className="font-medium text-gray-900 truncate max-w-[180px]">
                                                    {item.lead_name ?? <span className="text-gray-400 italic">sem nome</span>}
                                                </p>
                                                <p className="text-[11px] text-gray-400 font-mono">{item.sender_id}</p>
                                            </td>

                                            {/* Página */}
                                            <td className="px-4 py-3">
                                                <p className="font-medium text-gray-900 truncate max-w-[180px]">
                                                    {item.page_name ?? <span className="text-gray-400 italic">–</span>}
                                                </p>
                                                <p className="text-[11px] text-gray-400 font-mono">{item.page_id}</p>
                                            </td>

                                            {/* Grupo */}
                                            <td className="px-4 py-3">
                                                {item.group_broadcast_id != null ? (
                                                    <div className="flex items-center gap-1">
                                                        <span className="font-mono text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-0.5">
                                                            {item.group_broadcast_id}
                                                        </span>
                                                        <button
                                                            onClick={() =>
                                                                navigator.clipboard.writeText(String(item.group_broadcast_id))
                                                            }
                                                            className="p-0.5 rounded hover:bg-gray-100 text-gray-400"
                                                            title="Copiar ID do grupo"
                                                        >
                                                            <Copy className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-300 text-xs">–</span>
                                                )}
                                            </td>

                                            {/* Tentativas */}
                                            <td className="px-4 py-3 text-center">
                                                <span className="text-sm font-semibold text-gray-700">
                                                    {item.attempt_count}
                                                </span>
                                            </td>

                                            {/* Status */}
                                            <td className="px-4 py-3">
                                                <Badge
                                                    className={`${retryStatusColor[item.status]} border text-xs whitespace-nowrap`}
                                                >
                                                    {retryStatusLabel[item.status]}
                                                </Badge>
                                            </td>

                                            {/* Primeiro 551 */}
                                            <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                                                {formatInTz(item.first_551_at, BRT_TZ)}
                                            </td>

                                            {/* Último retry */}
                                            <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                                                {item.last_retry_at
                                                    ? formatInTz(item.last_retry_at, BRT_TZ)
                                                    : <span className="text-gray-300">–</span>}
                                            </td>

                                            {/* Resolvido em */}
                                            <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                                                {item.resolved_at
                                                    ? formatInTz(item.resolved_at, BRT_TZ)
                                                    : <span className="text-gray-300">–</span>}
                                            </td>

                                            {/* Tempo na esteira */}
                                            <td className="px-4 py-3 text-xs whitespace-nowrap">
                                                {item.resolved_at ? (
                                                    <span className="font-medium text-gray-700">{timeOnTrack(item)}</span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                                                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                                                        em andamento
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                            <span className="text-xs text-gray-400">
                                {total.toLocaleString('pt-BR')} registro{total !== 1 ? 's' : ''}
                            </span>
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-xl"
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                >
                                    Anterior
                                </Button>
                                <span className="text-sm text-gray-400">{page} de {totalPages}</span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-xl"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                >
                                    Próxima
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
